import React from 'react';
import { useNavigate } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import RasmusPortrait from '../assets/Rasmus.JPG';

const MEETING_LINK = 'https://meetings-eu1.hubspot.com/claus-moeldrup';

const RasmusKaltoft = () => {
  const navigate = useNavigate();

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Rasmus Kaltoft — Executive Coach | AICE"
        description="Coaching, der skaber reel forskel. For ledere, der er vant til at levere, og som kræver det samme af deres coach. PhD · Harvard · INSEAD."
        canonicalPath="/rasmus-kaltoft"
      />

      {/* ── HERO ── */}
      <section style={heroSection}>
        <div style={heroLeft}>
          <div style={credentialRow}>
            <span style={pillGold}>Executive Coach</span>
            <span style={pillNeutral}>PhD · Harvard · INSEAD</span>
          </div>
          <h1 style={heroTitle}>
            Coaching, der skaber<br />
            <em style={{ fontStyle: 'italic', color: '#C03626' }}>reel forskel.</em>
          </h1>
          <p style={heroSub}>
            For ledere, der er vant til at levere, og som kræver det samme af deres coach.
          </p>
          <div style={heroDivider} />
          <p style={heroLead}>
            Jeg stiller de spørgsmål, du helst ville undgå. Jeg husker, hvad du sagde ville gøre.
            Og jeg bruger <strong>20 år i topledelse</strong> og <strong>AI</strong> til at forberede mig professionelt og struktureret.
          </p>
          <div style={ctaRow}>
            <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <button type="button" style={primaryCta}>Book en gratis samtale</button>
            </a>
            <button type="button" style={ghostCta} onClick={() => document.getElementById('hvad')?.scrollIntoView({ behavior: 'smooth' })}>
              Se hvad der adskiller mig
            </button>
          </div>
        </div>
        <div style={heroRight}>
          <div style={photoWrap}>
            <img src={RasmusPortrait} alt="Rasmus Kaltoft" style={heroPhoto} />
            <div style={photoOverlay} />
            <div style={photoCaption}>
              <span style={quoteMark}>"</span>
              <p style={quoteText}>De fleste coaches følger din energi. Jeg følger dine resultater.</p>
              <div style={statsRow}>
                {[
                  { n: '20+', l: 'Års i topledelse' },
                  { n: 'PhD', l: 'Aalborg Univ.' },
                  { n: 'AI', l: 'Hvert forløb' },
                ].map(({ n, l }) => (
                  <div key={l} style={statCell}>
                    <span style={statN}>{n}</span>
                    <span style={statL}>{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div style={goldRule} />

      {/* ── DIFFERENTIATORS ── */}
      <section id="hvad" style={truthSection}>
        <div style={truthLeft}>
          <p style={sectionLabel}>Hvad er anderledes</p>
          <h2 style={truthH2}>
            Seks ting,<br />du ikke finder<br />andre <em style={{ fontStyle: 'italic', color: '#C03626' }}>steder.</em>
          </h2>
        </div>
        <div style={truthBlocks}>
          {[
            {
              n: '01',
              title: 'Jeg kender presset fra indsiden',
              text: 'CCO, EVP, VP, Director hos Pandora, FLSmidth og Teknologisk Institut. Jeg har siddet i de møder, truffet de beslutninger og mærket det pres, du beskriver. Det giver en type forståelse, ingen certificering kan erstatte.',
            },
            {
              n: '02',
              title: 'Jeg laver en plan, ikke en samtale',
              text: 'Vi definerer mål, delmål og konkrete næste skridt. Og jeg følger op. Hvad sagde du ville gøre sidst? Det spørgsmål stilles altid. Det er ubehageligt. Det er pointen.',
            },
            {
              n: '03',
              title: 'AI giver mig en fordel. Og dig.',
              text: 'Jeg bruger kunstig intelligens til forberedelse, mønstergenkendelse og opfølgning. Du får opsummeringer, personlighedsanalyser og dokumenteret progression. Effekten er synlig, ikke en fornemmelse.',
            },
            {
              n: '04',
              title: 'Krop og præstation er én ting',
              text: 'Udholdenhed, søvn, kost og restitution er ikke hyggeemner. De er variabler i din præstation. Jeg integrerer dem aktivt, fordi jeg selv lever dem, og fordi de er afgørende for din kapacitet som leder.',
            },
            {
              n: '05',
              title: 'Jeg er enormt nysgerrig',
              text: 'Jeg er lidt nørdet. Meget interesseret i mennesker. Jeg stiller de spørgsmål, du ikke stiller dig selv, og jeg giver ikke slip, før vi har fundet noget, der reelt rykker noget.',
            },
            {
              n: '06',
              title: 'Ja, jeg går i farverigt tøj',
              text: 'Det er ikke tilfældigt. Det er et signal om, at jeg ikke er den diplomatiske konsulent med neutrale svar. Jeg siger, hvad jeg ser. Direkte. Det går tæt på, og det er nyttigt.',
            },
          ].map(({ n, title, text }) => (
            <div key={n} style={truthBlock}>
              <span style={truthN}>{n}</span>
              <div>
                <h3 style={truthTitle}>{title}</h3>
                <p style={truthText}>{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── AI SECTION (dark) ── */}
      <section style={darkSection} id="ai">
        <div style={darkInner}>
          <div style={darkIntro}>
            <h2 style={darkH2}>
              AI er ikke<br />et <em style={{ fontStyle: 'italic', color: '#fff' }}>trick.</em><br />Det er en fordel.
            </h2>
            <p style={darkDesc}>
              Jeg bruger kunstig intelligens systematisk igennem hele forløbet, før, under og efter hver session.
            </p>
          </div>
          <div style={aiCards}>
            {[
              { tag: 'Forberedelse', title: 'Jeg husker alt', text: 'Velforberedt til hver session. Mønstre, aftaler og nuancer fra tidligere samtaler er alle med.' },
              { tag: 'Til dig', title: 'Opsummeringer & aftaler', text: 'Du forlader ikke en session og prøver at huske, hvad vi aftalte. Det er skrevet ned og sendt til dig.' },
              { tag: 'Analyse', title: 'Personlighedsanalyse', text: 'Baseret på mønstre i dine svar. Ikke standardtests, men noget der handler specifikt om dig.' },
              { tag: 'Dokumentation', title: 'Synlig effekt', text: 'Fra session 1 til session 8. Effekten er ikke en fornemmelse. Den er dokumenteret og synlig.' },
            ].map(({ tag, title, text }) => (
              <div key={title} style={aiCard}>
                <span style={aiCardTag}>{tag}</span>
                <h3 style={aiCardTitle}>{title}</h3>
                <p style={aiCardText}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VS TABLE ── */}
      <section style={vsSection}>
        <p style={sectionLabel}>Hvad du typisk møder, og hvad du møder her</p>
        <h2 style={vsH2}>
          Typisk coach.<br /><em style={{ fontStyle: 'italic', color: '#C03626' }}>Vs. mig.</em>
        </h2>
        <table style={vsTable}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
              <th style={vsHeader}></th>
              <th style={vsHeader}>Typisk coach</th>
              <th style={{ ...vsHeader, color: '#C03626' }}>Rasmus Kaltoft</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Forberedelse', 'Husker måske lidt fra sidst.', 'AI-assisteret forberedelse. Intet falder mellem stolene.'],
              ['Tilgang', 'Følger fokuspersonens energi i dag. Stoler på sin fornemmelse.', 'Struktureret GROW-ramme kombineret med nysgerrighed og dybe spørgsmål.'],
              ['Opfølgning', 'Ny session, nyt udgangspunkt.', 'Aktivt opfølgning på aftaler og mål fra forrige gang.'],
              ['Dokumentation', 'Ingen.', 'Opsummeringer, personlighedsanalyse og synlig progression.'],
              ['Erfaring', 'Oftest psykolog-, HR- eller terapeutbaggrund.', 'CCO, EVP, VP og Director. 20+ år i topledelse. PhD, Harvard, INSEAD.'],
              ['Kroppen', 'Sjældent på dagsordenen.', 'Sundhed, kost og restitution integreret som en del af din præstation.'],
              ['Direkthed', 'Diplomatisk. Følger din komfort.', 'Direkte. Udfordrer. Beder dig om at være konkret og forpligte dig.'],
            ].map(([label, them, me]) => (
              <tr key={label} style={vsRow}>
                <td style={vsCellLabel}>{label}</td>
                <td style={{ ...vsCell, fontStyle: 'italic', color: '#8c8880' }}>{them}</td>
                <td style={{ ...vsCell, color: '#1f1f1f', fontWeight: 500 }}>{me}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── PROCESS ── */}
      <section style={processSection} id="metode">
        <div style={processInner}>
          <div style={processHeader}>
            <h2 style={processH2}>
              Sådan ser<br />en session <em style={{ fontStyle: 'italic', color: '#C03626' }}>ud.</em>
            </h2>
            <p style={processDesc}>
              Hver time er struktureret, men ikke rigid. Vi ved, hvad vi skal ud af det, og vi når det.
            </p>
          </div>
          <div style={processSteps}>
            {[
              { n: 1, color: '#FFAA96', title: 'Mål for i dag', desc: 'Vi starter præcist. Hvad skal du gå ud ad døren med?' },
              { n: 2, color: '#FF6F61', title: 'Opfølgning', desc: 'Hvad aftalte vi sidst? Hvad skete der? Ingen slack her.' },
              { n: 3, color: '#E84A38', title: 'Dybe spørgsmål', desc: 'Dem du ikke stiller dig selv. Ubehagelige. Nødvendige.' },
              { n: 4, color: '#C03626', title: 'Egne svar', desc: 'Jeg holder rummet. Du finder løsningen. Den sidder bedre, når den er din.' },
              { n: 5, color: '#8a2419', title: 'Konkret plan', desc: 'Handlinger, ikke intentioner. Næste skridt, ansvar, tidsfrist.' },
            ].map(({ n, color, title, desc }) => (
              <div key={n} style={processStep}>
                <div style={{ ...stepNum, background: color }}>
                  {n}
                </div>
                <h3 style={stepName}>{title}</h3>
                <p style={stepDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BACKGROUND ── */}
      <section style={bgSection} id="baggrund">
        <p style={sectionLabel}>Legitimitet</p>
        <h2 style={bgH2}>
          Jeg taler<br />af <em style={{ fontStyle: 'italic', color: '#C03626' }}>erfaring.</em>
        </h2>
        <p style={bgIntro}>
          Jeg har ikke læst om topledelsespres i en bog. Jeg har stået i det, truffet beslutningerne og betalt prisen. Det er det, der giver mig troværdighed.
        </p>
        <div style={bgGrid}>
          {[
            {
              label: 'Erhvervserfaring',
              title: '20+ år i topledelse',
              text: 'CCO hos DBK, EVP hos Teknologisk Institut, VP Customer Success hos Marcura, Director of Operations hos Pandora, lederroller hos FLSmidth. Globale teams, store transformationer, svære beslutninger under pres.',
            },
            {
              label: 'Uddannelse',
              title: 'PhD, Harvard & INSEAD',
              text: 'PhD fra Aalborg Universitet. Executive Leadership Programs fra Harvard Business School og INSEAD. Et analytisk fundament der holder i mødet med kompleksitet.',
            },
            {
              label: 'Teknologi & sundhed',
              title: 'AI og kroppen som præstationsværktøjer',
              text: 'Jeg arbejder dagligt med kunstig intelligens, strategisk og praktisk. Og jeg lever sundhed, motion og kost, ikke som hobbyer, men som en del af min præstationsevne. Begge dele bruger jeg aktivt i coaching.',
            },
          ].map(({ label, title, text }) => (
            <div key={label} style={bgCell}>
              <span style={bgCellLabel}>{label}</span>
              <h3 style={bgCellTitle}>{title}</h3>
              <p style={bgCellText}>{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={ctaSection} id="kontakt">
        <p style={ctaEyebrow}>Klar til at starte</p>
        <h2 style={ctaH2}>
          Første samtale<br />er <em style={{ fontStyle: 'italic', color: '#fff' }}>gratis.</em>
        </h2>
        <p style={ctaText}>
          Ingen standardspørgsmål, ingen salgspitch. Vi bruger den første samtale på at vurdere hinanden:
          din situation, min tilgang, og om vores energi og arbejdsform passer sammen.
          Ingen forpligtelse, ingen beslutning under pres.
        </p>
        <div style={ctaBtns}>
          <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <button type="button" style={primaryCta}>Book en gratis samtale</button>
          </a>
          <a href="tel:+4561447767" style={{ textDecoration: 'none' }}>
            <button type="button" style={ghostCtaLight}>Ring: +45 61 44 77 67</button>
          </a>
        </div>
        <div style={ctaContact}>
          <a href="mailto:rka@aice.dk" style={ctaContactLink}>rka@aice.dk</a>
          <a href="tel:+4561447767" style={ctaContactLink}>+45 61 44 77 67</a>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>Hvidovre, Danmark</span>
        </div>
      </section>
    </div>
  );
};

export default RasmusKaltoft;

// ── Styles ──────────────────────────────────────────────

const pageStyle = {
  background: '#ffffff',
  minHeight: '100vh',
  color: '#0b0a08',
};

// Hero
const heroSection = {
  display: 'flex',
  minHeight: '90vh',
  flexWrap: 'wrap',
};

const heroLeft = {
  flex: '1 1 380px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: 'clamp(3rem, 8vw, 7rem) clamp(1.5rem, 6vw, 5rem)',
  gap: '1.25rem',
};

const credentialRow = {
  display: 'flex',
  gap: '0.75rem',
  flexWrap: 'wrap',
};

const pillBase = {
  fontFamily: 'monospace',
  fontSize: '0.72rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  padding: '0.35rem 0.85rem',
  border: '1px solid',
};

const pillGold = {
  ...pillBase,
  borderColor: '#C03626',
  color: '#C03626',
  background: '#FFF2EE',
};

const pillNeutral = {
  ...pillBase,
  borderColor: '#d8d2c6',
  color: '#8c8880',
};

const heroTitle = {
  fontFamily: 'Georgia, serif',
  fontWeight: 300,
  fontSize: 'clamp(2.5rem, 6vw, 5rem)',
  lineHeight: 1.0,
  letterSpacing: '-0.5px',
  margin: 0,
  color: '#0b0a08',
};

const heroSub = {
  fontSize: '1.1rem',
  fontWeight: 300,
  color: '#8c8880',
  fontStyle: 'italic',
  margin: 0,
  lineHeight: 1.5,
  maxWidth: '480px',
};

const heroDivider = {
  width: '60px',
  height: '1px',
  background: '#C03626',
};

const heroLead = {
  fontSize: '1rem',
  fontWeight: 300,
  lineHeight: 1.85,
  maxWidth: '500px',
  color: '#0b0a08',
  margin: 0,
};

const ctaRow = {
  display: 'flex',
  gap: '1.25rem',
  flexWrap: 'wrap',
  alignItems: 'center',
  marginTop: '0.5rem',
};

const primaryCta = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: '#fff',
  border: 'none',
  padding: '0.9rem 2rem',
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  boxShadow: '0 10px 24px rgba(192,54,38,0.25)',
};

const ghostCta = {
  background: 'transparent',
  color: '#8c8880',
  border: 'none',
  borderBottom: '1px solid #d8d2c6',
  padding: '0.9rem 0',
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

// Hero right / photo
const heroRight = {
  flex: '0 0 clamp(300px, 40vw, 500px)',
  position: 'relative',
  background: '#8a2419',
  minHeight: '500px',
};

const photoWrap = {
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: '500px',
  overflow: 'hidden',
};

const heroPhoto = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center top',
  display: 'block',
  filter: 'grayscale(15%) contrast(1.04)',
};

const photoOverlay = {
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to bottom, rgba(26,24,20,0.1) 0%, rgba(26,24,20,0) 40%, rgba(26,24,20,0.7) 100%)',
};

const photoCaption = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: '2.5rem 2rem',
  zIndex: 2,
};

const quoteMark = {
  fontFamily: 'Georgia, serif',
  fontSize: '4rem',
  lineHeight: 0.5,
  color: '#C03626',
  opacity: 0.6,
  display: 'block',
  marginBottom: '0.75rem',
};

const quoteText = {
  fontFamily: 'Georgia, serif',
  fontSize: '1.1rem',
  fontStyle: 'italic',
  fontWeight: 300,
  color: 'rgba(247,243,236,0.85)',
  lineHeight: 1.5,
  marginBottom: '1.5rem',
  margin: '0 0 1.5rem',
};

const statsRow = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  borderTop: '1px solid rgba(247,243,236,0.1)',
};

const statCell = {
  padding: '1rem',
  borderRight: '1px solid rgba(247,243,236,0.1)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.3rem',
};

const statN = {
  fontFamily: "'Arial Black', sans-serif",
  fontWeight: 700,
  fontSize: '1.6rem',
  color: '#C03626',
  lineHeight: 1,
};

const statL = {
  fontFamily: 'monospace',
  fontSize: '0.65rem',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'rgba(247,243,236,0.35)',
};

const goldRule = {
  height: '1px',
  background: 'linear-gradient(90deg, transparent 0%, #C03626 30%, #C03626 70%, transparent 100%)',
  opacity: 0.35,
};

// Differentiators
const truthSection = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '4rem',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'clamp(4rem, 8vw, 8rem) clamp(1.5rem, 6vw, 5rem)',
  alignItems: 'start',
};

const truthLeft = {
  position: 'sticky',
  top: '6rem',
};

const sectionLabel = {
  fontFamily: 'monospace',
  fontSize: '0.72rem',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#8c8880',
  marginBottom: '1.5rem',
};

const truthH2 = {
  fontFamily: 'Georgia, serif',
  fontWeight: 300,
  fontSize: 'clamp(2rem, 4vw, 3.5rem)',
  lineHeight: 1.1,
  letterSpacing: '-0.5px',
  color: '#0b0a08',
  margin: 0,
};

const truthBlocks = {
  display: 'flex',
  flexDirection: 'column',
};

const truthBlock = {
  display: 'grid',
  gridTemplateColumns: '40px 1fr',
  gap: '1.5rem',
  alignItems: 'start',
  padding: '2.5rem 0',
  borderTop: '1px solid #d8d2c6',
};

const truthN = {
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  color: '#C03626',
  paddingTop: '4px',
};

const truthTitle = {
  fontFamily: "'Arial Black', sans-serif",
  fontSize: '1.05rem',
  fontWeight: 700,
  marginBottom: '0.6rem',
  color: '#0b0a08',
  letterSpacing: '-0.1px',
};

const truthText = {
  fontSize: '0.95rem',
  fontWeight: 300,
  lineHeight: 1.78,
  color: '#4a4840',
  margin: 0,
};

// AI Section (dark)
const darkSection = {
  background: 'linear-gradient(135deg, #C03626 0%, #8a2419 100%)',
  color: '#fff',
  padding: 'clamp(4rem, 8vw, 8rem) clamp(1.5rem, 6vw, 5rem)',
  position: 'relative',
};

const darkInner = {
  maxWidth: '1200px',
  margin: '0 auto',
};

const darkIntro = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '3rem',
  marginBottom: '4rem',
  alignItems: 'end',
};

const darkH2 = {
  fontFamily: 'Georgia, serif',
  fontWeight: 300,
  fontSize: 'clamp(2.2rem, 5vw, 4rem)',
  lineHeight: 1.05,
  letterSpacing: '-0.5px',
  color: '#fff',
  margin: 0,
};

const darkDesc = {
  fontSize: '1rem',
  fontWeight: 300,
  lineHeight: 1.8,
  color: 'rgba(255,255,255,0.75)',
  fontStyle: 'italic',
  alignSelf: 'end',
};

const aiCards = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1px',
  background: 'rgba(255,255,255,0.12)',
  borderRadius: '12px',
  overflow: 'hidden',
};

const aiCard = {
  background: 'rgba(255,255,255,0.08)',
  padding: '2.5rem 2rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const aiCardTag = {
  fontFamily: 'monospace',
  fontSize: '0.68rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.55)',
};

const aiCardTitle = {
  fontFamily: "'Arial Black', sans-serif",
  fontSize: '1rem',
  fontWeight: 700,
  color: '#fff',
  margin: 0,
  lineHeight: 1.2,
};

const aiCardText = {
  fontSize: '0.88rem',
  fontWeight: 300,
  lineHeight: 1.75,
  color: 'rgba(255,255,255,0.7)',
  margin: 0,
};

// VS Table
const vsSection = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'clamp(4rem, 8vw, 8rem) clamp(1.5rem, 6vw, 5rem)',
};

const vsH2 = {
  fontFamily: 'Georgia, serif',
  fontWeight: 300,
  fontSize: 'clamp(2rem, 4vw, 3.5rem)',
  lineHeight: 1.05,
  letterSpacing: '-0.5px',
  margin: '0 0 3rem',
  color: '#0b0a08',
};

const vsTable = {
  width: '100%',
  borderCollapse: 'collapse',
};

const vsHeader = {
  fontFamily: 'monospace',
  fontSize: '0.72rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  padding: '1rem 1.5rem',
  textAlign: 'left',
  color: '#8c8880',
  fontWeight: 400,
  borderBottom: '1px solid #0b0a08',
};

const vsRow = {
  borderBottom: '1px solid #d8d2c6',
};

const vsCellLabel = {
  padding: '1.5rem 1.5rem',
  fontFamily: 'monospace',
  fontSize: '0.72rem',
  color: '#8c8880',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  verticalAlign: 'top',
  width: '140px',
};

const vsCell = {
  padding: '1.5rem 1.5rem',
  fontSize: '0.95rem',
  fontWeight: 300,
  lineHeight: 1.65,
  verticalAlign: 'top',
};

// Process
const processSection = {
  background: '#FFF9F8',
  borderTop: '1px solid #F1D5CF',
  borderBottom: '1px solid #F1D5CF',
  padding: 'clamp(4rem, 8vw, 8rem) clamp(1.5rem, 6vw, 5rem)',
};

const processInner = {
  maxWidth: '1200px',
  margin: '0 auto',
};

const processHeader = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '3rem',
  marginBottom: '4rem',
  alignItems: 'end',
};

const processH2 = {
  fontFamily: 'Georgia, serif',
  fontWeight: 300,
  fontSize: 'clamp(2rem, 4vw, 3.5rem)',
  lineHeight: 1.05,
  letterSpacing: '-0.5px',
  color: '#0b0a08',
  margin: 0,
};

const processDesc = {
  fontSize: '1rem',
  fontWeight: 300,
  lineHeight: 1.8,
  color: '#8c8880',
  fontStyle: 'italic',
  alignSelf: 'end',
  margin: 0,
};

const processSteps = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '2rem',
};

const processStep = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '1rem',
};

const stepNum = {
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Arial Black', sans-serif",
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#fff',
  flexShrink: 0,
};

const stepName = {
  fontFamily: "'Arial Black', sans-serif",
  fontSize: '0.9rem',
  fontWeight: 700,
  color: '#0b0a08',
  margin: 0,
};

const stepDesc = {
  fontSize: '0.88rem',
  fontWeight: 300,
  lineHeight: 1.65,
  color: '#8c8880',
  margin: 0,
};

// Background
const bgSection = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'clamp(4rem, 8vw, 8rem) clamp(1.5rem, 6vw, 5rem)',
};

const bgH2 = {
  fontFamily: 'Georgia, serif',
  fontWeight: 300,
  fontSize: 'clamp(2rem, 4vw, 3.5rem)',
  lineHeight: 1.05,
  letterSpacing: '-0.5px',
  margin: '0 0 1rem',
  color: '#0b0a08',
};

const bgIntro = {
  maxWidth: '640px',
  fontSize: '1.1rem',
  fontWeight: 300,
  fontStyle: 'italic',
  color: '#8c8880',
  lineHeight: 1.75,
  marginBottom: '3.5rem',
};

const bgGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '1px',
  background: '#F1D5CF',
  borderRadius: '16px',
  overflow: 'hidden',
  border: '1px solid #F1D5CF',
};

const bgCell = {
  background: '#fff',
  padding: '2.75rem 2.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const bgCellLabel = {
  fontFamily: 'monospace',
  fontSize: '0.68rem',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: '#C03626',
};

const bgCellTitle = {
  fontFamily: "'Arial Black', sans-serif",
  fontSize: '1.15rem',
  fontWeight: 700,
  color: '#0b0a08',
  margin: 0,
  lineHeight: 1.15,
};

const bgCellText = {
  fontSize: '0.92rem',
  fontWeight: 300,
  lineHeight: 1.75,
  color: '#5a5850',
  margin: 0,
};

// CTA
const ctaSection = {
  background: 'linear-gradient(135deg, #C03626 0%, #8a2419 100%)',
  padding: 'clamp(5rem, 10vw, 10rem) clamp(1.5rem, 6vw, 5rem)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1.5rem',
};

const ctaEyebrow = {
  fontFamily: 'monospace',
  fontSize: '0.72rem',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.6)',
  margin: 0,
};

const ctaH2 = {
  fontFamily: 'Georgia, serif',
  fontWeight: 300,
  fontSize: 'clamp(2.5rem, 7vw, 5rem)',
  lineHeight: 1.0,
  letterSpacing: '-0.5px',
  color: '#fff',
  margin: 0,
};

const ctaText = {
  fontSize: '1rem',
  fontWeight: 300,
  fontStyle: 'italic',
  lineHeight: 1.7,
  color: 'rgba(255,255,255,0.8)',
  maxWidth: '560px',
  margin: 0,
};

const ctaBtns = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginTop: '0.5rem',
};

const ghostCtaLight = {
  background: 'transparent',
  color: 'rgba(255,255,255,0.8)',
  border: '1px solid rgba(255,255,255,0.3)',
  padding: '0.9rem 2rem',
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const ctaContact = {
  display: 'flex',
  gap: '2.5rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  letterSpacing: '0.08em',
  marginTop: '1rem',
};

const ctaContactLink = {
  color: 'rgba(255,255,255,0.6)',
  textDecoration: 'none',
};
